import { flow, pipe } from 'fp-ts/lib/function';
import { EntityType } from '@/entities/entityType';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  addVersionForUpdate,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '../..';
import { encodeEntityTypeQuery, EntityTypeQuery } from '@/services/api/resources/entityType/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';

const baseUrl = '/v2/entity-types';
const filterUrl = '/v2/filters/entity-types';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const list = (query: EntityTypeQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeEntityTypeQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = () => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const create = (entityType: EntityType) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(entityType))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, entityType: Versioned<EntityType>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(entityType.version)),
  map(setBody(formCodec.encode(entityType.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
